import React, { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Modal, AutoComplete, Button, Input } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import _ from "lodash";
import { searchForRegistration } from "../../actions";
import { ContentTable } from "@authzsvc/common-react-components";

const RegistrationTable = ({
  allowsComment,
  rows,
  loading,
  reasons,
  onChangeReason,
  onSelectRow,
}) => {
  const columns = useMemo(() => {
    const base = [
      { title: "Client ID", dataIndex: "clientId", searchable: true },
      {
        title: "Description",
        dataIndex: "description",
        searchable: true,
        render: (text, record) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>{text}</span>
            <Button
              id="selectRegistrationButton"
              color="green"
              variant="filled"
              icon={<PlusOutlined />}
              onClick={() => onSelectRow(record, reasons[record.id] ?? null)}
            />
          </div>
        ),
      },
    ];

    if (!allowsComment) return base;

    return [
      ...base,
      {
        title: "Comment",
        key: "comment",
        render: (_, record) => (
          <Input
            value={reasons[record.id] ?? ""}
            onChange={(e) => onChangeReason(record.id, e.target.value)}
            placeholder="Token exchange request reason..."
          />
        ),
      },
    ];
  }, [allowsComment, reasons, onChangeReason, onSelectRow]);

  return (
    <ContentTable
      style={{ marginTop: 10 }}
      loading={loading}
      rowKey="id"
      columns={columns}
      dataSource={rows || []}
      pagination={false}
    />
  );
};

RegistrationTable.propTypes = {
  allowsComment: PropTypes.bool,
  rows: PropTypes.array,
  loading: PropTypes.bool,
  reasons: PropTypes.object.isRequired,
  onChangeReason: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
};

const RegistrationSearchModal = ({
  title,
  onSelect,
  searchForRegistration,
  authenticationProviderId,
  allowsComment = false,
  open,
  onOpenChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [reasons, setReasons] = useState({}); // { [registrationId]: reason }

  // reset state whenever the modal opens
  useEffect(() => {
    if (open) {
      setRows([]);
      setReasons({});
      setLoading(false);
    }
  }, [open]);

  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const handleSelectRow = useCallback(
    (record, reason) => {
      onSelect?.(record, reason || null);
      close();
    },
    [onSelect, close],
  );

  const onChangeReason = useCallback((id, value) => {
    setReasons((prev) => ({ ...prev, [id]: value }));
  }, []);

  const debouncedSearch = useMemo(
    () =>
      _.debounce(async (value = "") => {
        const q = value.trim();
        if (q.length < 2) {
          setRows([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          const resp = await searchForRegistration(authenticationProviderId, {
            filter: `registrationName:startswith:${q}`,
            sort: "registrationName",
          });
          const data = resp?.payload?.data ?? [];
          setRows(Array.isArray(data) ? data : []);
        } finally {
          setLoading(false);
        }
      }, 400),
    [searchForRegistration, authenticationProviderId],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  return (
    <Modal
      centered
      title={title}
      open={!!open}
      onCancel={close}
      footer={null}
      width={800}
      closable
    >
      <AutoComplete
        onChange={debouncedSearch}
        onSearch={debouncedSearch}
        allowClear
        options={[]}
        style={{ width: "100%" }}
        placeholder="Type at least 2 characters of the client ID"
      />
      <RegistrationTable
        allowsComment={allowsComment}
        rows={rows}
        loading={loading}
        reasons={reasons}
        onChangeReason={onChangeReason}
        onSelectRow={handleSelectRow}
      />
    </Modal>
  );
};

RegistrationSearchModal.propTypes = {
  title: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
  searchForRegistration: PropTypes.func.isRequired,
  authenticationProviderId: PropTypes.string,
  allowsComment: PropTypes.bool,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
};

export default connect(() => ({}), { searchForRegistration })(
  RegistrationSearchModal,
);
